import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined'
import { Box } from '@mui/material'
import { FC, ReactNode, useEffect, useState } from 'react'

import LinkTile from 'components/LinkTile'
import useActiveRoutePath from 'hooks/useActiveRoutePath'

interface MenuItemProps {
    label: string
    href?: string
    icon?: ReactNode
    subItems?: MenuItemProps[]
    target?: string
    onClick?: () => void
    dataTestId?: string
    tag?: string
}

export const MenuItem: FC<MenuItemProps> = ({
    label,
    href,
    icon,
    subItems,
    target,
    onClick,
    dataTestId,
    tag,
}) => {
    const [expand, setExpand] = useState<boolean>(false)
    const activeRoute = useActiveRoutePath()

    useEffect(() => {
        if (subItems) {
            const hasActiveSubItem = subItems.some((subItem) => subItem.href === activeRoute)
            if (hasActiveSubItem) {
                setExpand(true)
            }
        }
    }, [activeRoute])

    const handleMainItemClick = () => {
        if (subItems) {
            setExpand(!expand)
        } else {
            onClick?.()
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            <LinkTile
                data-testid={dataTestId}
                href={href}
                onClickCallback={handleMainItemClick}
                icon={icon}
                target={target}
                rightIcon={
                    subItems ? (
                        <ArrowDropDownOutlinedIcon
                            sx={{
                                transform: `rotate(${expand ? '180deg' : '0'})`,
                                transition: 'transform .2s cubic-bezier(0.4, 0, 1, 1)',
                                transformOrigin: 'center',
                            }}
                        />
                    ) : undefined
                }
                label={tag}
            >
                {label}
            </LinkTile>
            {subItems && (
                <Box
                    sx={{
                        pl: 5,
                        height: expand
                            ? `${subItems.length * 40 + (subItems.length - 1) * 8}px`
                            : 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {subItems.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                position: 'relative',

                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: '-20px',
                                    top: '-36px',
                                    width: '40px',
                                    height: '56px',
                                    border: `1px solid`,
                                    borderColor: `Surface600`,
                                    borderTop: 0,
                                    borderRight: 0,
                                    borderRadius: '0 0 0 8px',
                                },
                            }}
                        >
                            <LinkTile
                                key={index}
                                href={item.href}
                                onClickCallback={item.onClick}
                                data-testid={item.dataTestId}
                                label={item.tag}
                            >
                                {item.label}
                            </LinkTile>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

const Menu: FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {children}
        </Box>
    )
}

export default Menu
